import React, { useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useImmer } from 'use-immer';
import produce from 'immer';
import { CourseComponent } from './course-component';
import { ICourseState, ICourseStateErrors } from '../../interfaces';
import { YesNo } from '../../../../components/radio-group/yes-no-component';
import { ICompanyLookup } from '../../../../components/lookups/company-lookup/interfaces';
import { ClassroomDto, CourseDto, CreateCourseDto, EducatorDto, OfferLookupDto } from '../../../../api-service/clients';
import { attendanceAreaPathName, getCourseAttendanceStatisticsPath, getLessonRedirectPath } from '../../attendance-area-pathnames';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { usePostToTab } from '../../../search/hooks/usePostToTab';
import { validateCourse } from '../../validation';
import { isNullOrUndefined } from '../../../../utilities/null-undefined-helper';
import { IUserLookup } from '../../../../components/lookups';
import { useMetaTitle } from '../../../../hooks';
import { useAuthenticatedUser } from '../../../../authentication';
import { REFRESH_COURSE_LESSON_TABLE } from '../../utils';
import { UpdateFutureLessonsDialog } from '../components/update-future-lessons-dialog';
import { useIsAllowed } from '../../../../authentication/use-is-allowed';
import { ResourceOperations, ResourceTypes } from '../../../../authentication/resources';
import { useAttendanceRights } from '../../use-attendance-rights';
import { ConfirmDialog } from '../../../../components/confirm-dialog';
import { getCannotBeDeletedReason } from './course-deletion-reason';

export const REFRESH_COMPANY_COURSE_TABLE = 'REFRESH_COMPANY_COURSE_TABLE';

const attendanceCheckFields = [
  'earliestArrivalOnTimeBeforeStart',
  'latestArrivalOnTimeAfterStart',
  'latestAttendanceRegistrationAfterStart',
  'earliestDepartureRegistrationBeforeEnd',
  'earliestDepartureOnTimeBeforeEnd',
  'latestDepartureOnTimeAfterEnd',
] as const;

const initial = {
  hasLimitedSeats: false,
  attendanceCheckStart: false,
  attendanceCheckEnd: false,
  createNegativeEventOnMissingAttendance: true,
  createFollowUpAdvisOnMissingAttendance: false,
  createAdvisIfNotOnTime: false,
  hasDutyToWork: false,
  requiresAttendanceApproval: false,
} as ICourseState;

function hasAttendanceCheckChanged(savedCourse: CourseDto, updatedCourse: CourseDto) {
  return attendanceCheckFields.some(field => (savedCourse[field] ?? undefined) !== (updatedCourse[field] ?? undefined));
}

const attendanceClient = moduleClientFactory.createAttendanceClient();
const offerClient = moduleClientFactory.createOfferAndActivityClient(true);
const userClient = moduleClientFactory.createLookupUserClient();

export function Course() {
  const { courseId } = useParams();
  const { search } = useLocation();
  const companyIdQuery = new URLSearchParams(search.toLocaleLowerCase()).get('companyid') || undefined;
  const offerIdQuery = new URLSearchParams(search.toLocaleLowerCase()).get('offerid') || undefined;

  const [course, setCourse] = useImmer<ICourseState>({ ...initial, companyId: companyIdQuery, offerId: offerIdQuery, educatorIds: [] });
  const [errors, setErrors] = useState<ICourseStateErrors>({} as ICourseStateErrors);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSavingPublished, setIsSavingPublished] = useState<boolean>(false);
  const [classRooms, setClassRooms] = useState<ClassroomDto[]>([]);
  const [educators, setEducators] = useState<EducatorDto[]>([]);
  const [isSavingCancelled, setIsSavingCancelled] = useState<boolean>(false);
  const [isDeletingLessons, setIsDeletingLessons] = useState<boolean>(false);
  const [isDeleteCourseDialogOpen, setIsDeleteCourseDialogOpen] = useState<boolean>(false);
  const [isDeletingCourse, setIsDeletingCourse] = useState<boolean>(false);
  const [cannotBeDeletedReason, setCannotBeDeletedReason] = useState<string>();
  const [savedCourse, setSavedCourse] = useState<CourseDto>();
  const [courseAwaitingLessonChoice, setCourseAwaitingLessonChoice] = useState<CourseDto>();

  const authenticatedUser = useAuthenticatedUser();
  const { canEditCourse } = useAttendanceRights();
  useMetaTitle(courseId ? `Redigér hold - ${course.name}` : 'Opret hold');
  const postUpdate = usePostToTab(REFRESH_COMPANY_COURSE_TABLE);
  const canSeeOffer = useIsAllowed([{ resourceType: ResourceTypes.Offer, resourceOperation: ResourceOperations.Read }]);

  usePostToTab<CourseDto>(REFRESH_COURSE_LESSON_TABLE, () => {
    handleRefreshLessons();
  });

  useEffect(() => {
    async function loadData() {
      try {
        setIsLoading(true);
        const userId = authenticatedUser?.profile.id;
        if (courseId) {
          // Load course data
          const result = await attendanceClient.getCourseById({ courseId });
          const { course: courseResult } = result;
          if (courseResult) {
            const lessonsResult = await attendanceClient.getLessonsByCourseId({ courseId });
            const { lessons } = lessonsResult;
            const { companyId, offerId, capacity, earliestArrivalOnTimeBeforeStart, earliestDepartureOnTimeBeforeEnd } = courseResult;

            const hasLimitedSeats = !!capacity;
            const attendanceCheckStart = !isNullOrUndefined(earliestArrivalOnTimeBeforeStart);
            const attendanceCheckEnd = !isNullOrUndefined(earliestDepartureOnTimeBeforeEnd);
            setCourse({ ...courseResult, companyId, courseId, offerId, hasLimitedSeats, attendanceCheckStart, attendanceCheckEnd, lessons: lessons || [] });
            setSavedCourse(courseResult);
            await Promise.all([loadEducatorsAndClassRooms(companyId), loadCourseDeletability()]);
            if (offerId) {
              loadOfferCapacity(offerId);
            }
          }
        } else if (userId) {
          const userResult = await userClient.getUserById({ userId });
          const teamId = userResult.user?.teamId;

          setCourse(draft => {
            draft.responsibleCaseworkerId = userId;
            draft.responsibleTeamId = teamId;
          });
        }

        if (companyIdQuery) {
          await loadEducatorsAndClassRooms(companyIdQuery);
        }

        if (canSeeOffer && offerIdQuery) {
          const offerResult = await offerClient.getOfferSimpleDetailsByOfferId({ offerId: offerIdQuery });
          if (offerResult.offer) {
            setCourse(draft => {
              const { offer } = offerResult;
              draft.name = offer?.title || '';
              draft.companyId = offer?.companyId;
              draft.offerId = offer?.offerId;
              draft.hasLimitedSeats = true;
              draft.capacity = offer?.capacity || 1;
              draft.offerCapacity = offer?.capacity;
              draft.responsibleCaseworkerId = offer?.responsibleUserId;
              draft.responsibleTeamId = offer?.responsibleTeamId;
              draft.description = offer?.internalDescription || '';
            });
            if (offerResult.offer?.companyId) {
              await loadEducatorsAndClassRooms(offerResult.offer.companyId);
            }
          }
        }
      } catch (error) {
        // Handle errors
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    }
    loadData();
  }, []);

  async function loadEducatorsAndClassRooms(companyId: string) {
    const [educatorResult, classRoomResult] = await Promise.all([
      attendanceClient.getEducatorsByCompanyId({ companyId }),
      attendanceClient.getClassRoomsByCompanyId({ companyId }),
    ]);
    setEducators(educatorResult.educators || []);
    setClassRooms(classRoomResult.classRooms || []);
  }

  async function loadCourseDeletability() {
    const deletability = await attendanceClient.getCourseDeletability({ courseId });
    setCannotBeDeletedReason(getCannotBeDeletedReason(deletability));
  }

  async function loadOfferCapacity(offerId: string) {
    try {
      if (canSeeOffer) {
        const offerResult = await offerClient.getOfferSimpleDetailsByOfferId({ offerId });
        setCourse(draft => {
          if (draft.offerId === offerId) {
            draft.offerCapacity = offerResult.offer?.capacity;
          }
        });
      }
    } catch (error) {
      console.error(error);
    }
  }

  async function handleCreateCourseClick() {
    try {
      setIsLoading(true);
      const { companyId } = course;
      if (companyId) {
        const newCourse = produce(course, draft => {
          draft.isCompleted = false;
          draft.isDeleted = false;

          if (!draft.attendanceCheckStart) {
            draft.earliestArrivalOnTimeBeforeStart = undefined;
            draft.latestArrivalOnTimeAfterStart = undefined;
            draft.latestAttendanceRegistrationAfterStart = undefined;
          }

          if (!draft.attendanceCheckEnd) {
            draft.earliestDepartureRegistrationBeforeEnd = undefined;
            draft.earliestDepartureOnTimeBeforeEnd = undefined;
            draft.latestDepartureOnTimeAfterEnd = undefined;
          }
        }) as CreateCourseDto;

        await attendanceClient.createCourse({ course: newCourse });
        postUpdate(window.opener);
        window.close();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }

  function handleUpdateCourseClick() {
    const { companyId } = course;
    if (!companyId) {
      return;
    }

    const updatedCourse = produce(course, draft => {
      draft.companyId = companyId;
      draft.courseId = courseId;

      if (!draft.attendanceCheckStart) {
        draft.earliestArrivalOnTimeBeforeStart = undefined;
        draft.latestArrivalOnTimeAfterStart = undefined;
        draft.latestAttendanceRegistrationAfterStart = undefined;
      }

      if (!draft.attendanceCheckEnd) {
        draft.earliestDepartureRegistrationBeforeEnd = undefined;
        draft.earliestDepartureOnTimeBeforeEnd = undefined;
        draft.latestDepartureOnTimeAfterEnd = undefined;
      }
    }) as CourseDto;

    if (savedCourse && hasAttendanceCheckChanged(savedCourse, updatedCourse)) {
      setCourseAwaitingLessonChoice(updatedCourse);
      return;
    }

    saveCourse(updatedCourse, false);
  }

  async function saveCourse(updatedCourse: CourseDto, updateFutureLessons: boolean) {
    try {
      setIsLoading(true);
      await attendanceClient.updateCourse({ course: updatedCourse, updateFutureLessons });
      postUpdate(window.opener);
      window.close();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }

  function handleUpdateFutureLessonsChoice(updateFutureLessons: boolean) {
    if (courseAwaitingLessonChoice) {
      saveCourse(courseAwaitingLessonChoice, updateFutureLessons);
    }
  }

  async function handleFinishCourse() {
    try {
      setIsLoading(true);
      const { isCompleted } = course;
      await attendanceClient.updateCourseStatus({ courseId, isCompleted: !isCompleted });
      setCourse({ ...course, isCompleted: !isCompleted });
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleDeleteCourse() {
    try {
      setIsDeletingCourse(true);
      await attendanceClient.deleteCourse({ courseId });
      postUpdate(window.opener);
      window.close();
    } catch (error) {
      console.error(error);
    } finally {
      setIsDeletingCourse(false);
      setIsDeleteCourseDialogOpen(false);
    }
  }

  async function handleRefreshLessons() {
    try {
      setIsLoading(true);
      const [{ lessons }] = await Promise.all([attendanceClient.getLessonsByCourseId({ courseId }), loadCourseDeletability()]);
      setCourse({ ...course, lessons });
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }

  function handleValidate() {
    const [isValid, tmpErrors] = validateCourse(course);
    setErrors({ ...tmpErrors });
    return isValid;
  }
  function handleYesNoChange<K extends keyof ICourseState>(name: K, value: YesNo) {
    setCourse((draft: ICourseState) => {
      (draft as any)[name] = value === YesNo.Yes;
      if (name === 'hasLimitedSeats' && value === YesNo.No) {
        draft.capacity = undefined;
      }
    });
  }

  function handleCaseWorkerChange(caseWorker?: IUserLookup) {
    setCourse((draft: ICourseState) => {
      draft.responsibleCaseworkerId = caseWorker?.userIdentifier;
      if (caseWorker?.userIdentifier) {
        draft.responsibleTeamId = caseWorker.teamId;
      }
    });
  }

  function handleTeamChange(teamId?: string) {
    setCourse((draft: ICourseState) => {
      draft.responsibleTeamId = teamId;
      if (!teamId) {
        draft.responsibleCaseworkerId = undefined;
      }
    });
  }
  function handleStringChange<K extends keyof ICourseState>(name: K, value: string) {
    setCourse((draft: ICourseState) => {
      (draft as any)[name] = value;
    });
  }

  function handleNumberFieldChange<K extends keyof ICourseState>(name: K, value?: number) {
    setCourse((draft: ICourseState) => {
      (draft as any)[name] = value;
    });
  }

  async function handleCompanyChange(value?: ICompanyLookup) {
    if (value?.id) {
      const classRoomResult = await attendanceClient.getClassRoomsByCompanyId({ companyId: value.id });
      setClassRooms(classRoomResult.classRooms || []);
      const educatorResult = await attendanceClient.getEducatorsByCompanyId({ companyId: value.id });
      setEducators(educatorResult.educators || []);
    } else {
      setClassRooms([]);
      setEducators([]);
    }
    setCourse((draft: ICourseState) => {
      draft.companyId = value?.id;
      draft.offerId = undefined;
      draft.offerCapacity = undefined;
      draft.classroomId = undefined;
    });
    setErrors(prev => ({ ...prev, capacity: undefined }));
  }

  function handleOfferChange(value?: OfferLookupDto): void {
    setCourse((draft: ICourseState) => {
      draft.offerId = value?.offerId;
      draft.offerCapacity = undefined;
      if (value?.companyId) {
        draft.companyId = value?.companyId;
      }
    });
    setErrors(prev => ({ ...prev, capacity: undefined }));
    if (value?.offerId) {
      loadOfferCapacity(value.offerId);
    }
  }

  function handleAddLesson() {
    window.open(`${attendanceAreaPathName}/${getLessonRedirectPath()}?courseId=${courseId}`);
  }

  function handleOpenStatistics() {
    window.open(`${attendanceAreaPathName}/${getCourseAttendanceStatisticsPath(courseId)}`);
  }
  async function handlePublishDraftLessonClicked(lessonIds: string[]) {
    try {
      setIsSavingPublished(true);
      await attendanceClient.publishLessons({ lessonIds });
      await handleRefreshLessons();
    } catch (error) {
      console.error(error);
    } finally {
      setIsSavingPublished(false);
    }
  }
  async function handleCancelLessonsClicked(lessonIds: string[], cancellationReason: string) {
    try {
      setIsSavingCancelled(true);
      await attendanceClient.cancelLessons({ lessonIds, cancellationReason });
      await handleRefreshLessons();
    } catch (error) {
      console.error(error);
    } finally {
      setIsSavingCancelled(false);
    }
  }
  async function handleDeleteLessonsClicked(lessonIds: string[]) {
    try {
      setIsDeletingLessons(true);
      await attendanceClient.deleteLessons({ lessonIds });
      await handleRefreshLessons();
    } catch (error) {
      console.error(error);
    } finally {
      setIsDeletingLessons(false);
    }
  }

  function handleEducatorsChange(value: EducatorDto[]) {
    setCourse(draft => {
      draft.educatorIds = value.map(x => x.educatorId);
    });
  }

  return (
    <>
      <CourseComponent
        course={course}
        isEditmode={!!courseId}
        isLoading={isLoading}
        onCreateCourseClick={handleCreateCourseClick}
        onUpdateCourseClick={handleUpdateCourseClick}
        onValidate={handleValidate}
        onYesNoChange={handleYesNoChange}
        onStringChange={handleStringChange}
        onNumberFieldChange={handleNumberFieldChange}
        onCompanyChange={handleCompanyChange}
        onOfferChange={handleOfferChange}
        onFinishCourse={handleFinishCourse}
        onDeleteCourse={() => setIsDeleteCourseDialogOpen(true)}
        isDeletingCourse={isDeletingCourse}
        cannotBeDeletedReason={cannotBeDeletedReason}
        errors={errors}
        onAddLesson={handleAddLesson}
        onOpenStatistics={courseId ? handleOpenStatistics : undefined}
        onRefreshLessons={handleRefreshLessons}
        classRooms={classRooms}
        educators={educators}
        onPublishDraftLessonClicked={handlePublishDraftLessonClicked}
        isSavingPublished={isSavingPublished}
        onCancelLessonsClicked={handleCancelLessonsClicked}
        isSavingCancelled={isSavingCancelled}
        onDeleteLessonsClicked={handleDeleteLessonsClicked}
        isDeletingLessons={isDeletingLessons}
        onEducatorsChange={handleEducatorsChange}
        onCaseWorkerChange={handleCaseWorkerChange}
        onTeamChange={handleTeamChange}
        canSeeOffer={canSeeOffer}
        hasAttendanceWrite={canEditCourse}
      />
      <UpdateFutureLessonsDialog
        isOpen={!!courseAwaitingLessonChoice}
        isLoading={isLoading}
        onUpdateLessonsClick={() => handleUpdateFutureLessonsChoice(true)}
        onKeepLessonsClick={() => handleUpdateFutureLessonsChoice(false)}
      />
      {isDeleteCourseDialogOpen && (
        <ConfirmDialog
          open
          onConfirmClick={handleDeleteCourse}
          title="Vil du slette dette hold?"
          confirmButtonText="Ja, slet"
          onCancelClick={() => setIsDeleteCourseDialogOpen(false)}
          cancelButtonText="Afbryd"
          type="warning"
        />
      )}
    </>
  );
}
